import {
  FraudDecision,
  IPolicyValidatorResponse,
  NewVerifyDecision,
  PolicyValidatorAddress,
  PolicyValidatorShipment,
} from '@src/models/api';
import { ReturnFraudType } from '@src/constants/analytics';
import { ItemToReturn, ReturnsFraudPrompt } from '@src/models/returns';
import { ShipToAddress } from '@src/models/finalize-return';
import { IReturnShipments } from '@src/stores/returnStore';
import {
  RETURN_METHOD,
  RETURN_METHOD_TO_SHIPMENT_MAP,
} from '@src/constants/general';
import { AdaptedReturnMethods } from './eligibility-utils';
import {
  checkFraudType,
  generateInStoreShipment,
  generateSorisShipment,
  getFraudMessage,
  getReturnsFraudPrompts,
} from '.';
import { TagOnApprissDeniedOrWarned } from './analytics-tagging';

export const processAprissVerifyDecision = (
  hasIneligibleItem: boolean,
  setReturnFraudType: (returnFraudType: ReturnFraudType) => void,
  setRenderModal: (returnsFraudPrompt: ReturnsFraudPrompt) => void,
  closeModal: () => void,
  proceedToNextStep: () => void,
  setFraudDecisions: (fraudDecisions: FraudDecision[]) => void,
  multiVerifyDecision?: NewVerifyDecision,
): ReturnFraudType | undefined => {
  if (!multiVerifyDecision) return undefined;
  const fraudDecisions = multiVerifyDecision.fraudDecisions;
  let fraudType = checkFraudType(multiVerifyDecision);

  if (fraudType) {
    const fraudMessage = getFraudMessage(multiVerifyDecision);
    if (fraudType === 'WARNED' && hasIneligibleItem) {
      fraudType = 'WARNEDINVALID';
    }
    setReturnFraudType(fraudType);
    setRenderModal(
      getReturnsFraudPrompts(fraudMessage, closeModal, proceedToNextStep)?.[
        fraudType
      ],
    );
    TagOnApprissDeniedOrWarned(fraudType);
  }

  // Persist fraudDecisions for finalize call
  setFraudDecisions(fraudDecisions);
  return fraudType;
};

/**
 * Determines if the return contains items with multiple vendor return addresses and updates the following state.
 *
 * - Creates a mapping of each lineItemId to its vendor return address.
 * - Tracks unique addresses to determine if more than one is present.
 * - Migrates existing shipment states (lorpContactDetails, hasAgreed, boxQuantity) to new shipments when applicable
 * - Matches shipments based on lineItems to handle cases where shipments are added/removed
 *
 * @param {HrtvResponseLineItem[]} ssrHrtvRespLineItems - Array of line items from the SSR HRTV response.
 * @param {(isMultiAddressReturn: boolean) => void} setIsMultiAddressReturn
 * @param {(setVendorReturnAddresses: VendorReturnAddresses[]) => void} setVendorReturnAddresses
 * @param {IReturnShipments[]} currentShipments - Current shipments to preserve states from
 */
export const processPolicyValidatorShipments = (
  response: IPolicyValidatorResponse,
  TagPolicyValidatorMultiAddress: () => void,
  currentShipments?: IReturnShipments[],
): IReturnShipments[] => {
  const returnShipments =
    response.returnService.ssrHRTVResponse?.shipments || [];

  // Add shipmentId to each return shipment for easier identification later
  const modifiedReturnShipments = returnShipments.map((shipment, index) => {
    const shipmentId = index + 1;

    // Find matching shipment from current state based on line items
    const existingShipment = currentShipments?.find((currentShipment) => {
      // grab a line item from shipment and see if it exists in currentShipment
      const lineItemKey = shipment.lineItems[0]?.lineItemId;
      return currentShipment.lineItems.some(
        (lineItem) => lineItem.lineItemId === lineItemKey,
      );
    });
    // Reaching here means the selection changed and the legs were rebuilt, and
    // the shipping service has not been re-asked about any of it. A box floor
    // is its answer about a particular set of lines at a particular weight, so
    // none of it survives: the same lines at a different quantity weigh
    // something else, and the match above is "does the existing leg contain
    // this new leg's first line", so on a split both halves would otherwise
    // inherit the whole group's floor and cost the customer more labels than
    // the service ever asked for.
    //
    // The suite id goes with it, and that one matters more. It names a group
    // the service formed, and the match above is "does the existing leg hold
    // this new leg's first line" — so a rebuilt leg would otherwise keep the
    // name of a group it no longer describes. The create request keys the
    // chosen box count to that name, so keeping it hands one suite a count for
    // lines it never grouped, and leaves the other suite's count unsent while
    // that suite's items are still in the request.
    //
    // Dropped rather than recomputed. The service owns both numbers, so the
    // honest state after an edit is not knowing them; the picker falls back to
    // one until a fresh grouping supplies it, and no count is keyed to a suite
    // until one is named again. Re-asking on edit is separate work, and until
    // it lands this is the safe direction to be wrong in — a floor of one
    // constrains nothing, where a stale one constrains wrongly.
    //
    // The count the customer chose is deliberately not dropped with them: it is
    // their entry rather than the service's answer, and the shipment-shaped
    // request carries it on the leg itself, so clearing it here would make them
    // re-choose after every edit on a path that submits it correctly today.
    //
    // That leaves the screen outliving the request under the item-list shape.
    // The review card and its drawer both read this count, so after an edit the
    // customer still sees the number they picked while the request carries
    // none — the count is known and shown, and only its keying is gone. Worth
    // knowing before reading the create builder's note that no count is the
    // honest post-edit state: that is true of the request, not the screen.
    // Asking the shipping service again on edit is what makes the two agree,
    // and is separate work.
    return {
      ...existingShipment,
      minBoxes: undefined,
      rlcReturnSuite: undefined,
      shipmentId,
      lineItems: shipment.lineItems,
      shipmentType: shipment.shipmentType,
      shipToAddress: shipment.shipToAddress,
    };
  });

  if (returnShipments.length > 1) {
    TagPolicyValidatorMultiAddress();
  }
  return modifiedReturnShipments;
};

/**
 * A shipment leg before it has been placed in the list and given its number.
 *
 * "Leg" means two different things in this codebase, so it is worth saying
 * which is which:
 *
 * - a **shipment leg**, this one — a single destination's worth of items. A
 *   return can hold several of them for one method: two drop-off addresses are
 *   two legs.
 * - a **submission leg** — one create call, of which there is exactly one per
 *   return method. That is the sense `useMultiCallCreateReturn` uses, and the
 *   sense the create and finalize acceptance criteria are written in.
 *
 * Both arrived with the same epic, which is how they came to share a word.
 * Where the difference matters, say which one is meant.
 */
export type UnnumberedReturnShipment = Omit<IReturnShipments, 'shipmentId'>;

/**
 * The per-line values the create call still reads off the leg rather than off
 * the item. The methods contract replaced them with a single `rtvItemId`, but
 * nothing maps that into them until the create call is rebuilt around an item
 * list, so a composed leg has to be given them from somewhere.
 */
export type LegacyLineIdentifiers = Pick<
  PolicyValidatorShipment['lineItems'][number],
  'rtvTokenId' | 'returnDispCode' | 'returnPolicyId'
>;

/**
 * Indexes the per-line identifiers on a set of legs by line item.
 *
 * Only the three the create call reads are taken. `itemEligibility` in
 * particular is left behind: a composed leg takes that from the service's
 * answer, which is the point of composing it.
 */
export const collectLineIdentifiers = (
  shipments: Pick<PolicyValidatorShipment, 'lineItems'>[],
): Record<string, LegacyLineIdentifiers> =>
  Object.fromEntries(
    shipments.flatMap((shipment) =>
      shipment.lineItems.map(
        ({ lineItemId, rtvTokenId, returnDispCode, returnPolicyId }) => [
          lineItemId,
          // Absent fields are dropped rather than carried as undefined, so
          // spreading this onto a line cannot blank one it already had.
          Object.fromEntries(
            Object.entries({
              rtvTokenId,
              returnDispCode,
              returnPolicyId,
            }).filter(([, value]) => value !== undefined),
          ),
        ],
      ),
    ),
  );

/**
 * Indexes each leg's return address by the lines travelling on it.
 *
 * The methods contract marks the per-method address optional, and the endpoint
 * serving it is not built yet, so a composed leg can come out with no
 * destination. The create call puts that address on the wire, so a leg without
 * one submits a return nobody can route. This gives the composer the
 * validator's answer to fall back on.
 */
export const collectLineAddresses = (
  shipments: Pick<PolicyValidatorShipment, 'lineItems' | 'shipToAddress'>[],
): Record<string, PolicyValidatorAddress> =>
  Object.fromEntries(
    shipments.flatMap((shipment) =>
      shipment.shipToAddress
        ? shipment.lineItems.map((line) => [
            line.lineItemId,
            shipment.shipToAddress as PolicyValidatorAddress,
          ])
        : [],
    ),
  );

/**
 * The service's address, with the two fields its contract cannot carry taken
 * from the validator's.
 *
 * Not `??`, because the two are not equivalent in content: the methods
 * contract has no shipToName and no emailId, so the adapters fill both with
 * blanks while the validator's answer holds real values. Taking the service's
 * address whole would blank two fields that reach the create request today — a
 * change to what is submitted, when all that should change is how items group.
 *
 * Narrow to those two on purpose, rather than "any blank field yields". A
 * blank is not a general signal of silence: `addressLine2` is optional on the
 * service's contract and required on the validator's, so an empty one means a
 * return centre with no suite line, not an unanswered question. Yielding it
 * would put the validator's suite on the service's street — an address in
 * neither answer, on the request that routes the parcel.
 *
 * Every other field is therefore taken from one address or the other, whole.
 * With no validator address to fall back on the service's is returned as it
 * stands, blanks included; a line the validator gave no address for has no
 * shipToAddress today either.
 */
const withFallbackAddress = (
  address?: PolicyValidatorAddress,
  fallback?: PolicyValidatorAddress,
): PolicyValidatorAddress | undefined => {
  if (!address) return fallback;
  if (!fallback) return address;
  return {
    ...address,
    shipToName: address.shipToName || fallback.shipToName,
    emailId: address.emailId || fallback.emailId,
  };
};

const addressGroupKey = (address?: PolicyValidatorAddress) => {
  // A method with no address groups separately from one whose address is
  // present but blank — the client-generated legs use blank strings.
  if (!address) return 'NO_ADDRESS';
  return [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.zipCode,
    address.country,
    address.contactName,
    address.dayPhone,
  ]
    .map((field) => (field || '').trim().toLowerCase())
    .join('|');
};

/**
 * Widens the adapted per-method address to the shape a shipment leg carries.
 * `emailId` has no source in the methods contract — the destination's address
 * is not the shopper's — so it is left blank, matching the client-generated
 * in-store leg.
 */
const toShipmentAddress = (
  address: ShipToAddress | undefined,
): PolicyValidatorAddress | undefined =>
  address && {
    ...address,
    addressLine2: address.addressLine2 || '',
    emailId: address.emailId || '',
  };

/** The methods legs can be composed for: parcel drop-off and large-item pickup. */
export type ComposableReturnMethod =
  | typeof RETURN_METHOD.UPS_DROP_OFF
  | typeof RETURN_METHOD.LORP;

/** Every composable method. Callers wanting a subset pass their own list. */
const ALL_COMPOSABLE_METHODS: readonly ComposableReturnMethod[] = [
  RETURN_METHOD.UPS_DROP_OFF,
  RETURN_METHOD.LORP,
];

/**
 * The service answers per line, so the leg the create call needs has to be
 * assembled here rather than read off the response.
 *
 * Drop-off lines sharing a return address belong on one leg, and the same
 * method to two addresses is two legs. Pickup does not group at all: every
 * line is its own leg even when two share an address. That asymmetry is the
 * backend's, not an invention here — `getAddressAndLineIdKeyForUniqueShipments`
 * in `HRTVPolicyValidatorService` appends the line item id to the key for
 * PICKUP alone, "so that each PICKUP item is its own shipment", and its test
 * table pins four pickup lines across two addresses to four shipments.
 *
 * Only parcel drop-off and large-item pickup are built here. In-store and SORIS
 * legs are client-generated and addressless, and are added by the composer.
 *
 * `methods` narrows that further. Drop-off grouping is moving to the shipping
 * service's own answer rather than this address key, so until that arrives the
 * caller asks for pickup alone and carries the drop-off legs over from the
 * validator instead. Passing a subset is the supported way to
 * stage that: the address key here is a stand-in for drop-off, not its
 * eventual source.
 */
export const groupMethodsIntoShipments = (
  selectedItems: ItemToReturn[],
  adaptedMethods: AdaptedReturnMethods,
  methods: readonly ComposableReturnMethod[] = ALL_COMPOSABLE_METHODS,
): UnnumberedReturnShipment[] => {
  const legsByKey = new Map<string, UnnumberedReturnShipment>();

  selectedItems.forEach((item) => {
    const returnMethod = item.returnMethod;
    if (
      returnMethod !== RETURN_METHOD.UPS_DROP_OFF &&
      returnMethod !== RETURN_METHOD.LORP
    ) {
      return;
    }
    // Kept as a second check rather than folded into the one above: that one
    // is what narrows the type for the shipment-type lookup below, and a
    // membership test on a runtime list cannot do that.
    if (!methods.includes(returnMethod)) return;
    const shipmentType = RETURN_METHOD_TO_SHIPMENT_MAP[returnMethod];
    const method = adaptedMethods[item.lineItemId]?.methods[returnMethod];
    const shipToAddress = toShipmentAddress(method?.shipToAddress);
    // The legacy per-line identifiers are deliberately not populated: the
    // methods contract replaced them, and nothing can supply them here. The
    // caller gives them to the leg afterwards, from the validator's answer.
    //
    // `itemEligibility` is carried for shape parity with a validator leg and
    // has no reader: the ineligible-items check runs on the validator's own
    // shipments, not on these. Ineligible lines are still caught there.
    const lineItem = {
      lineItemId: item.lineItemId,
      itemEligibility: Boolean(method?.eligible),
    };

    // Pickup keys on the line as well as the address, so it never groups. See
    // the asymmetry described above.
    //
    // The key sees only the service's address; the validator fallback is
    // applied later, by the composer. So with drop-off composed here, two
    // drop-off lines the service gave no address for would key alike, collapse
    // into one leg, and take the first line's fallback — two lines bound for
    // different return centres shipping to one. Unreachable while the caller
    // asks for pickup alone, and moot if drop-off is re-keyed off the shipping
    // service's own grouping rather than an address. Worth knowing before
    // widening the method list.
    const key =
      returnMethod === RETURN_METHOD.LORP
        ? `${shipmentType}|${addressGroupKey(shipToAddress)}|${item.lineItemId}`
        : `${shipmentType}|${addressGroupKey(shipToAddress)}`;
    const existingLeg = legsByKey.get(key);
    if (existingLeg) {
      existingLeg.lineItems.push(lineItem);
      return;
    }
    // The key is omitted rather than set to undefined: the composer spreads a
    // leg over the shipment whose state it is preserving, and an explicit
    // undefined would blank an address that shipment already had.
    legsByKey.set(key, {
      shipmentType,
      ...(shipToAddress ? { shipToAddress } : {}),
      lineItems: [lineItem],
    });
  });

  return Array.from(legsByKey.values());
};

/**
 * Assembles the full shipment list from its sources — the legs composed from
 * the service's per-item answers, any legs carried over wholesale from another
 * builder, and the client-generated in-store and SORIS legs — and preserves
 * the state the customer already entered on a matching leg.
 *
 * `lineIdentifiers` exists for the same staging. The create call still reads
 * the RTV token, disposition code and policy id off the leg, and a composed
 * leg has none — the methods contract replaced them with a single `rtvItemId`
 * that nothing maps into them until the create call is rebuilt around an item
 * list. Giving the composed lines the validator's values keeps the create
 * payload as it is today, so composing changes how items are grouped and nothing else.
 *
 * `carriedOverLegs` exists for the staged migration. Drop-off grouping is
 * moving to the shipping service rather than to the address key used here, so
 * until that arrives the caller composes pickup alone and hands the
 * validator's drop-off legs through this parameter. They arrive already
 * preserved by the builder that produced them, so they skip the preservation
 * pass below and are only renumbered — running it twice would let one leg
 * inherit another's entries on top of its own.
 *
 * Preservation applies to the composed legs only. The client-generated legs are
 * minted fresh each time, as the policy-validator flow also does, so a store
 * leg can never inherit a box count, pickup date or contact details from a
 * parcel or pickup leg it has nothing to do with.
 *
 * A leg is matched by shipment type AND a shared line item. Type matters
 * because a line moving between methods would otherwise let the new leg
 * inherit the old method's entries; any shared line rather than the first
 * because a leg that gains an item ahead of the shared one would otherwise
 * lose the customer's entries entirely.
 *
 * Every leg is numbered in one final pass over the merged list. Numbering the
 * fetched legs and the client legs independently can hand two legs the same
 * shipmentId, and `setUpdatedShipmentFields` keys on that number.
 */
export const composeReturnShipments = (
  selectedItems: ItemToReturn[],
  adaptedMethods: AdaptedReturnMethods,
  TagMultiAddress: () => void,
  currentShipments?: IReturnShipments[],
  options?: {
    /** Which methods to compose legs for. Defaults to drop-off and pickup. */
    methods?: readonly ComposableReturnMethod[];
    /** Legs built elsewhere, already state-preserved, to renumber alongside. */
    carriedOverLegs?: UnnumberedReturnShipment[];
    /** Per-line identifiers to give composed lines, by line item id. */
    lineIdentifiers?: Record<string, LegacyLineIdentifiers>;
    /** Return addresses to fall back on when the service gave none. */
    fallbackAddresses?: Record<string, PolicyValidatorAddress>;
  },
): IReturnShipments[] => {
  const { lineIdentifiers, fallbackAddresses } = options ?? {};
  const composedLegs = groupMethodsIntoShipments(
    selectedItems,
    adaptedMethods,
    options?.methods,
  ).map((leg) => {
    const lineItems = lineIdentifiers
      ? leg.lineItems.map((lineItem) => ({
          ...lineItem,
          ...lineIdentifiers[lineItem.lineItemId],
        }))
      : leg.lineItems;
    // Every line on a leg shares its address, so the first that has a fallback
    // answers for the leg. Merged rather than chosen — see withFallbackAddress.
    const shipToAddress = withFallbackAddress(
      leg.shipToAddress,
      leg.lineItems
        .map((lineItem) => fallbackAddresses?.[lineItem.lineItemId])
        .find(Boolean),
    );
    return {
      ...leg,
      ...(shipToAddress ? { shipToAddress } : {}),
      lineItems,
    };
  });
  const carriedOverLegs = options?.carriedOverLegs ?? [];

  const clientLegs: UnnumberedReturnShipment[] = [];
  const hasInStore = selectedItems.some(
    (item) => item.returnMethod === RETURN_METHOD.IN_STORE,
  );
  const hasSoris = selectedItems.some(
    (item) => item.returnMethod === RETURN_METHOD.SORIS,
  );
  if (hasInStore) clientLegs.push(generateInStoreShipment(selectedItems, []));
  if (hasSoris) clientLegs.push(generateSorisShipment(selectedItems, []));

  // Counts exactly the legs the validator's own builder counted, so the switch
  // being on or off reports the same thing and the migration can be compared
  // like for like.
  //
  // SORIS is in that reckoning and the store leg is not, which looks arbitrary
  // until you see where each comes from. The validator synthesises a SORIS
  // shipment into the response it counts, whereas the Phase 0 store leg is
  // minted by the caller *after* the count has been taken. Composing mints
  // both, so the store leg has to be left out by hand to match.
  //
  // It counts legs rather than distinct addresses, despite its name, and so
  // fires for two pickup items going to one address — pickup is one leg per
  // line item. That over-report is the validator's too: the backend keys pickup
  // on the line item, so its response already carried two shipments and this
  // tag already fired on them. Reproduced rather than corrected; correcting it
  // is an analytics change with its own blast radius.
  const legsTheValidatorWouldCount =
    composedLegs.length +
    carriedOverLegs.length +
    clientLegs.filter((leg) => leg.shipmentType === 'SORIS').length;
  if (legsTheValidatorWouldCount > 1) {
    TagMultiAddress();
  }

  const preservedComposedLegs = composedLegs.map((leg) => {
    const existingShipment = currentShipments?.find(
      (currentShipment) =>
        currentShipment.shipmentType === leg.shipmentType &&
        currentShipment.lineItems.some((lineItem) =>
          leg.lineItems.some(
            (newLineItem) => newLineItem.lineItemId === lineItem.lineItemId,
          ),
        ),
    );
    // The spread carries the customer's own entries — box quantity, pickup
    // date, LORP contact details, the acknowledgement checkbox, text opt-in —
    // which is why it is a spread rather than a list of copied fields.
    //
    // The match is per leg with nothing marking an existing leg as taken, so
    // when one leg splits into two — same method, two addresses — both new
    // legs inherit the same entries, and a box count entered once is counted
    // twice. Carried over verbatim from the shipment builder this replaces,
    // whose behaviour the current tests pin, so it is left rather than quietly
    // changed here.
    //
    // Unreachable as this is called today: a split needs a leg's address to
    // change, only pickup legs are composed, and the only mid-flow edits are
    // quantity and reason. It becomes reachable the moment drop-off is composed
    // here too, and belongs to whatever turns that on.
    return { ...existingShipment, ...leg };
  });

  return [...preservedComposedLegs, ...carriedOverLegs, ...clientLegs].map(
    (leg, index) => ({
      ...leg,
      shipmentId: index + 1,
    }),
  );
};

// Utility function to get items from selectedItems that belong to a specific shipment
export const getItemToReturnItemsFromShipment = (
  shipment: IReturnShipments,
  selectedItems: ItemToReturn[],
) =>
  selectedItems.filter((selectedItem) =>
    shipment.lineItems.some(
      (shipmentLineItem) =>
        shipmentLineItem.lineItemId === selectedItem.lineItemId,
    ),
  );
